import classNames from 'classnames';
import { ScrollPanel } from 'primereact/scrollpanel';
import React, { useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { APP_BASENAME } from '../config';
import AppMenu from '../components/AppMenu';
import PageNotFound from '../components/PageNotFound';
import AppTopbar from '../components/AppTopbar';
import SideAccountSelector from '../components/SideAccountSelector';
import AccountsIndex from './AccountsIndex';
import MenuBuilder from '../services/apps/side-menu-builders/MenuBuilder';
import AccountRoutes from './AccountRoutes';
import { addLoggedAccount } from '../reducers/account';

const getEdgeForCurrentAccount = (edges, currentAccountId) => {
  let edgeForCurrentAccount;

  edges.forEach((edge) => {
    const accountId = edge.node.id;

    if (accountId === currentAccountId) {
      edgeForCurrentAccount = edge;
    }
  });

  return edgeForCurrentAccount;
};

const DEFAULT_ACCOUNT_STATE = {
  accountId: null,
  type: null,
  isHeadOfFamily: false,
  isUnlimited: false,
  superMatches: [],
  subscriptionInformation: {
    payedBy: null,
    ownerOfPayingAccount: null,
    hasTrialPeriod: false,
    hasActiveSubscription: false,
  },
};

const Index = ({ edges }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const addAccount = (account) => dispatch(addLoggedAccount(account));

  const [staticMenuInactive, setStaticMenuInactive] = useState(false);
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);
  const [userRole, setUserRole] = useState(null);

  const isDesktop = () => window.innerWidth > 1024;

  const { id, type, isHeadOfFamily, subscriptionInformation, gpiRepresentativeData, superMatches } = account;

  const payedBy = subscriptionInformation ? subscriptionInformation.payedBy : null;

  const menu = MenuBuilder(id, userRole, type, payedBy, isHeadOfFamily, superMatches, edges).buildMenu();

  const onAccountSelect = (selectedAccountId) => {
    const edgeForCurrentAccount = getEdgeForCurrentAccount(edges, selectedAccountId);

    setAccount(edgeForCurrentAccount.node);
    setUserRole(edgeForCurrentAccount.role);

    addAccount(selectedAccountId);
    history.push(`/accounts/${selectedAccountId}`);
  };

  const onToggleMenu = (event) => {
    const isDesktopMode = isDesktop();

    if (isDesktopMode) {
      setStaticMenuInactive(!staticMenuInactive);
    } else {
      setMobileMenuActive(!mobileMenuActive);
    }

    event.preventDefault();
  };

  const onMenuItemClick = (event) => {
    if (!event.item.items) {
      setMobileMenuActive(false);
    }
  };

  const selectInitializeAccount = (selectedAccountId) => {
    const edgeForCurrentAccount = getEdgeForCurrentAccount(edges, selectedAccountId);

    setAccount(edgeForCurrentAccount.node);
    setUserRole(edgeForCurrentAccount.role);

    addAccount(selectedAccountId);
  };

  const wrapperClasses = classNames('layout-wrapper', {
    'layout-static': true,
    'layout-static-sidebar-inactive': staticMenuInactive,
    'layout-mobile-sidebar-active': mobileMenuActive,
  });

  const logo = `${APP_BASENAME}/images/gpi-logo-white.svg`;

  const sidebarClassName = classNames('layout-sidebar', 'layout-sidebar-dark');

  return (
    <div className={wrapperClasses} role="presentation">
      <AppTopbar onToggleMenu={onToggleMenu} accountId={id} />
      <div className={sidebarClassName} role="presentation">
        <ScrollPanel style={{ height: '100%' }}>
          <div className="layout-sidebar-scroll-content">
            <div className="layout-logo">
              <div className="p-grid">
                <div className="p-col-12">
                  <img alt="Logo" src={logo} />
                </div>
                <div className="p-col-12">
                  <SideAccountSelector currentAccount={id} onAccountSelect={onAccountSelect} />
                </div>
              </div>
            </div>
            {id && <AppMenu model={menu} onMenuItemClick={onMenuItemClick} />}
          </div>
        </ScrollPanel>
      </div>
      <div className="layout-main">
        <Switch>
          <Route exact path="/" render={() => <AccountsIndex onAccountSelect={onAccountSelect} />} />
          <Route
            path="/accounts/:aid"
            render={() => (
              <AccountRoutes
                onAccountSelect={selectInitializeAccount}
                type={type}
                isHeadOfFamily={isHeadOfFamily}
                userRole={userRole}
                subscriptionInformation={subscriptionInformation}
                gpiRepresentativeData={gpiRepresentativeData}
              />
            )}
          />
          <Route component={PageNotFound} />
        </Switch>
      </div>
      <div className="layout-mask" />
    </div>
  );
};

Index.propTypes = {
  edges: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default Index;
